/**
 * Feature flags utility for managing feature toggles
 */
import { apiFetch } from './api';

// Cache for feature flags to avoid repeated API calls
let featureFlagsCache: any = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch feature flags from the API
 * @returns {Promise<Object>} Feature flags object
 */
export async function fetchFeatureFlags() {
  try {
    const response = await apiFetch('/api/config/feature-flags');

    if (response.ok) {
      const flags = await response.json();

      // Update cache
      featureFlagsCache = flags;
      cacheTimestamp = Date.now();

      return flags;
    } else {
      console.error('Failed to fetch feature flags:', response.status);
      // Return default values on error
      return getDefaultFeatureFlags();
    }
  } catch (error) {
    console.error('Error fetching feature flags:', error);
    // Return default values on error
    return getDefaultFeatureFlags();
  }
}

/**
 * Get feature flags with caching
 * @returns {Promise<Object>} Feature flags object
 */
export async function getFeatureFlags() {
  // Check if cache is valid
  if (featureFlagsCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
    return featureFlagsCache;
  }

  // Fetch fresh data
  return await fetchFeatureFlags();
}

/**
 * Get default feature flags (fallback values)
 * @returns {Object} Default feature flags
 */
function getDefaultFeatureFlags() {
  return {
    enable_admin_sync_buttons: true, // Default to enabled for backward compatibility
  };
}

/**
 * Check if a specific feature flag is enabled
 * @param {string} flagName - Name of the feature flag
 * @returns {Promise<boolean>} True if the feature is enabled
 */
export async function isFeatureEnabled(flagName) {
  const flags = await getFeatureFlags();
  return flags[flagName] ?? false;
}

/**
 * Clear the feature flags cache (useful for testing or manual refresh)
 */
export function clearFeatureFlagsCache() {
  featureFlagsCache = null;
  cacheTimestamp = null;
}
